import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
} from '@mui/material';
import { alpha, lighten, darken } from '@mui/material/styles';
import {
  Lock as LockIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PWAInstallPrompt from '../components/Common/PWAInstallPrompt';
import PWAInstallButton from '../components/Common/PWAInstallButton';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        // Check user role and validate access
        const userRole = result.user?.role_name || localStorage.getItem('userRole');
        
        // Validate that only Admin, SuperAdmin, and Teacher can access
        const allowedRoles = ['Admin', 'SuperAdmin', 'Teacher'];
        if (!allowedRoles.includes(userRole)) {
          setError('Access denied. This portal is only for Admin, and Teacher only.');
          // Clear any stored data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('userRole');
          localStorage.removeItem('userEmail');
          return;
        }
        
        // If role is allowed, redirect accordingly
        if (userRole === 'Teacher') {
          navigate('/teacher-dashboard');
        } else if (userRole === 'Admin' || userRole === 'SuperAdmin') {
          navigate('/dashboard');
        }
      } else {
        setError(result.message || 'Login failed');
        // Clear any stored data on failed login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userEmail');
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'An unexpected error occurred';
      setError(errorMessage);
      // Clear any stored data on error
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userEmail');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth={false} disableGutters>
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: { xs: 6, md: 8 },
          px: { xs: 3, md: 0 },
          background: (theme) => {
            const base = theme.palette.primary.main;
            const deepTone = darken(base, 0.35);
            const midTone = base;
            const lightTone = lighten(base, 0.25);
            return `linear-gradient(130deg, ${alpha(deepTone, 0.9)} 0%, ${alpha(midTone, 0.88)} 48%, ${alpha(
              lightTone,
              0.85
            )} 100%)`;
          },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -160,
            right: -140,
            width: 460,
            height: 460,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(220,0,78,0.35) 0%, rgba(220,0,78,0) 70%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -220,
            left: -160,
            width: 560,
            height: 560,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 70%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(160deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 45%)',
          }}
        />
        <Paper
          elevation={3}
          sx={{
            position: 'relative',
            p: 4,
            width: '100%',
            maxWidth: 400,
            borderRadius: 2,
            backdropFilter: 'blur(6px)',
            backgroundColor: 'rgba(255,255,255,0.96)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Box
              component="img"
              src="/logo1921.png"
              alt="School Logo"
              sx={{
                width: 80,
                height: 80,
                mb: 2,
                objectFit: 'contain',
              }}
            />
            <Typography component="h1" variant="h4" gutterBottom>
            St. Thomas School
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ letterSpacing: 1 }}>
              Login Portal
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>

            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ fontSize: '0.75rem' }}>
                Install our app for a better experience with offline access and faster loading
              </Typography>
              <PWAInstallButton variant="outlined" size="small" />
            </Box>
          </Box>
        </Paper>
      </Box>
      <PWAInstallPrompt />
    </Container>
  );
};

export default Login;
